import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Smartphone, Car, AlertTriangle, ArrowUpCircle } from 'lucide-react';
import { getAppConfigs, updateAppConfig } from '../../api/appConfig';
import { useAppConfigModal } from '../../context/AppConfigModalContext';
import { useToast } from '../../context/ToastContext';
import { getErrorMessage } from '../../lib/utils';
import Modal from '../ui/Modal';
import Card from '../ui/Card';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Textarea from '../ui/Textarea';
import { Skeleton } from '../ui/Skeleton';

const APP_META = {
  customer: { label: 'Customer App', icon: Smartphone },
  driver: { label: 'Driver App', icon: Car },
};

function formFromConfig(config) {
  return {
    maintenance_enabled: config.maintenance_enabled,
    maintenance_title: config.maintenance_title,
    maintenance_message: config.maintenance_message,
    force_update_enabled: config.force_update_enabled,
    force_update_min_build_android: String(config.force_update_min_build_android ?? 0),
    force_update_min_build_ios: String(config.force_update_min_build_ios ?? 0),
    force_update_url_android: config.force_update_url_android ?? '',
    force_update_url_ios: config.force_update_url_ios ?? '',
    force_update_title: config.force_update_title,
    force_update_message: config.force_update_message,
  };
}

function AppConfigCard({ config, onSave, saving }) {
  const meta = APP_META[config.app_name] ?? { label: config.app_name, icon: Smartphone };
  const Icon = meta.icon;
  const [form, setForm] = useState(() => formFromConfig(config));

  useEffect(() => {
    setForm(formFromConfig(config));
  }, [config]);

  function set(key, val) {
    setForm((f) => ({ ...f, [key]: val }));
  }

  function handleSave() {
    onSave({
      maintenance_enabled: form.maintenance_enabled,
      maintenance_title: form.maintenance_title,
      maintenance_message: form.maintenance_message,
      force_update_enabled: form.force_update_enabled,
      force_update_min_build_android: Number(form.force_update_min_build_android) || 0,
      force_update_min_build_ios: Number(form.force_update_min_build_ios) || 0,
      force_update_url_android: form.force_update_url_android,
      force_update_url_ios: form.force_update_url_ios,
      force_update_title: form.force_update_title,
      force_update_message: form.force_update_message,
    });
  }

  return (
    <Card>
      <div className="flex items-center gap-2.5 mb-5">
        <div className="rounded-xl bg-amber-100 dark:bg-amber-900/30 p-2.5">
          <Icon size={18} className="text-amber-600 dark:text-amber-400" />
        </div>
        <h3 className="font-semibold text-gray-900 dark:text-gray-100">{meta.label}</h3>
      </div>

      <div className="space-y-6">
        <section>
          <div className="flex items-center gap-3 mb-3">
            <input
              type="checkbox"
              id={`${config.app_name}_maintenance`}
              checked={form.maintenance_enabled}
              onChange={(e) => set('maintenance_enabled', e.target.checked)}
              className="rounded border-gray-300 text-amber-600 focus:ring-amber-500"
            />
            <label htmlFor={`${config.app_name}_maintenance`} className="flex items-center gap-1.5 text-sm font-medium text-gray-700 dark:text-gray-300">
              <AlertTriangle size={14} className="text-amber-500" />
              Maintenance Mode
            </label>
          </div>
          <div className="grid grid-cols-1 gap-3 pl-7">
            <Input label="Title" value={form.maintenance_title} onChange={(e) => set('maintenance_title', e.target.value)} />
            <Textarea label="Message" value={form.maintenance_message} onChange={(e) => set('maintenance_message', e.target.value)} />
          </div>
        </section>

        <section>
          <div className="flex items-center gap-3 mb-3">
            <input
              type="checkbox"
              id={`${config.app_name}_force_update`}
              checked={form.force_update_enabled}
              onChange={(e) => set('force_update_enabled', e.target.checked)}
              className="rounded border-gray-300 text-amber-600 focus:ring-amber-500"
            />
            <label htmlFor={`${config.app_name}_force_update`} className="flex items-center gap-1.5 text-sm font-medium text-gray-700 dark:text-gray-300">
              <ArrowUpCircle size={14} className="text-amber-500" />
              Force Update
            </label>
          </div>
          <div className="grid grid-cols-2 gap-3 pl-7">
            <Input label="Min Build (Android)" type="number" value={form.force_update_min_build_android} onChange={(e) => set('force_update_min_build_android', e.target.value)} />
            <Input label="Min Build (iOS)" type="number" value={form.force_update_min_build_ios} onChange={(e) => set('force_update_min_build_ios', e.target.value)} />
          </div>
          <div className="grid grid-cols-1 gap-3 pl-7 mt-3">
            <Input label="Store URL (Android)" value={form.force_update_url_android} onChange={(e) => set('force_update_url_android', e.target.value)} />
            <Input label="Store URL (iOS)" value={form.force_update_url_ios} onChange={(e) => set('force_update_url_ios', e.target.value)} />
            <Input label="Title" value={form.force_update_title} onChange={(e) => set('force_update_title', e.target.value)} />
            <Textarea label="Message" value={form.force_update_message} onChange={(e) => set('force_update_message', e.target.value)} />
          </div>
        </section>

        <div className="flex justify-end">
          <Button onClick={handleSave} loading={saving}>Save</Button>
        </div>
      </div>
    </Card>
  );
}

// No direct menu entry by design — reachable only by asking the AI
// assistant (mirrors how the server-logs viewer works), not a routed page.
export default function AppConfigModal() {
  const { isOpen, close } = useAppConfigModal();
  const { addToast } = useToast();
  const qc = useQueryClient();

  const { data, isLoading, isError, error, refetch, isRefetching } = useQuery({
    queryKey: ['app-config'],
    queryFn: () => getAppConfigs().then((r) => r.data),
    enabled: isOpen,
    retry: false,
  });

  const update = useMutation({
    mutationFn: ({ app_name, ...rest }) => updateAppConfig(app_name, rest),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['app-config'] }); addToast('App config updated', 'success'); },
    onError: () => addToast('Update failed', 'error'),
  });

  const configs = Array.isArray(data) ? data : [];

  return (
    <Modal isOpen={isOpen} onClose={close} title="App Config" size="xl">
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-5">
        Control maintenance mode and forced updates for each app independently.
      </p>
      {isLoading ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Skeleton className="h-96" />
          <Skeleton className="h-96" />
        </div>
      ) : isError ? (
        <div className="flex flex-col items-center gap-3 py-12 text-center">
          <p className="text-sm font-medium text-red-600 dark:text-red-400">
            {getErrorMessage(error, 'Failed to load app config')}
          </p>
          <p className="text-xs text-gray-400 max-w-sm">
            This usually means the app_configs table is missing on the backend
            (pending database migration) rather than a problem in the panel.
          </p>
          <Button variant="secondary" size="sm" onClick={() => refetch()} loading={isRefetching}>
            Retry
          </Button>
        </div>
      ) : configs.length === 0 ? (
        <p className="text-center text-sm text-gray-400 py-12">No app configs found.</p>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {configs.map((config) => (
            <AppConfigCard
              key={config.app_name}
              config={config}
              saving={update.isPending && update.variables?.app_name === config.app_name}
              onSave={(payload) => update.mutate({ app_name: config.app_name, ...payload })}
            />
          ))}
        </div>
      )}
    </Modal>
  );
}
